import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Images as ImagesIcon } from "lucide-react";
import FileUploader from "@/components/FileUploader";
import { useAuth } from "@/context/AuthContext";

const inputCls =
  "w-full bg-white border border-[#e5e0d8] text-[#1c1a17] rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#234333] focus:border-[#234333]";

const emptyForm = {
  id: "",
  title: "",
  caption: "",
  image_url: "",
  image_public_id: "",
  order: 0,
  status: "Active",
};

function Field({ label, children, full }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-2">{label}</div>
      {children}
    </label>
  );
}

export default function HeroSlides() {
  const { role } = useAuth();
  const canEdit = ["super_admin", "editor"].includes(role);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/hero-slides");
      setItems(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load hero slides");
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openAdd = () => { setForm({ ...emptyForm, order: items.length }); setOpen(true); };
  const openEdit = (p) => {
    setForm({
      id: p.id,
      title: p.title || "",
      caption: p.caption || "",
      image_url: p.image_url || "",
      image_public_id: p.image_public_id || "",
      order: p.order || 0,
      status: p.status || "Active",
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.image_url) return toast.error("Please upload an image");
    const payload = {
      title: (form.title || "").trim(),
      caption: (form.caption || "").trim(),
      image_url: form.image_url || "",
      image_public_id: form.image_public_id || "",
      order: Number(form.order) || 0,
      status: form.status === "Inactive" ? "Inactive" : "Active",
    };
    setSaving(true);
    try {
      if (form.id) {
        await api.put(`/admin/hero-slides/${form.id}`, payload);
        toast.success("Hero slide updated");
      } else {
        await api.post("/admin/hero-slides", payload);
        toast.success("Hero slide added");
      }
      setOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/admin/hero-slides/${confirmDelete.id}`);
      toast.success("Hero slide deleted");
      setConfirmDelete(null);
      fetchAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-hero-slides">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-semibold">Trust CMS</div>
          <h1 className="font-serif text-2xl text-[#1c1a17]">Home Hero Slider</h1>
          <div className="text-sm text-[#6b655b] mt-0.5">
            Images shown on the right of the Trust website home page. Slides rotate every 12 seconds.
          </div>
          <div className="mt-3 inline-flex flex-wrap items-center gap-x-4 gap-y-1 bg-[#f5f1e8] border border-[#e5e0d8] rounded-sm px-3 py-2 text-[12px] text-[#5b5650]">
            <span><span className="font-semibold text-[#1c1a17]">Required size:</span> <span className="font-mono text-[#0B1F4D] font-bold">1200 × 1500 px</span> (portrait · 4:5 ratio)</span>
            <span className="hidden md:inline text-[#bcb6aa]">·</span>
            <span><span className="font-semibold text-[#1c1a17]">Max file size:</span> 300 KB</span>
            <span className="hidden md:inline text-[#bcb6aa]">·</span>
            <span><span className="font-semibold text-[#1c1a17]">Formats:</span> JPG / PNG / WEBP</span>
          </div>
          <div className="text-[11px] text-[#a44f21] mt-1 font-semibold">⚠ Images not in 4:5 ratio will be cropped to fit. Other valid sizes: 800×1000, 960×1200, 1600×2000 px.</div>
        </div>
        {canEdit && (
          <button
            type="button"
            onClick={openAdd}
            data-testid="adm-hero-slide-add-btn"
            className="inline-flex items-center gap-2 bg-[#234333] hover:bg-[#1a3326] text-white px-4 py-2.5 text-sm font-semibold rounded-sm transition-colors"
          >
            <Plus size={16} strokeWidth={1.8} /> Add Slide
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-sm text-[#6b655b]">Loading…</div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-sm border border-[#e5e0d8] p-10 text-center">
          <ImagesIcon size={36} className="mx-auto text-[#bcb6aa]" strokeWidth={1.2} />
          <div className="mt-3 text-sm text-[#6b655b]">No hero slides yet. The home page will fall back to a default visual until you add at least one.</div>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {items.map((p) => (
            <div key={p.id} data-testid={`adm-hero-slide-card-${p.id}`} className="bg-white border border-[#e5e0d8] rounded-sm overflow-hidden flex flex-col">
              <div className="aspect-[4/5] bg-[#f5f1e8] overflow-hidden relative">
                {p.image_url ? (
                  <img src={p.image_url} alt={p.title || "slide"} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[#bcb6aa]"><ImagesIcon size={36} /></div>
                )}
                {p.status !== "Active" && (
                  <div className="absolute top-2 left-2 bg-amber-100 text-amber-800 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm font-semibold">Inactive</div>
                )}
                <div className="absolute top-2 right-2 bg-[#0B1F4D] text-white text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm font-semibold">#{p.order}</div>
              </div>
              <div className="p-3 flex-1 flex flex-col">
                {p.title && <div className="font-semibold text-[#1c1a17] text-sm leading-snug">{p.title}</div>}
                {p.caption && <div className="mt-1 text-xs text-[#5b5650] line-clamp-2">{p.caption}</div>}
                {canEdit && (
                  <div className="mt-auto pt-2 flex items-center gap-1.5">
                    <button type="button" onClick={() => openEdit(p)} data-testid={`adm-hero-slide-edit-${p.id}`} className="inline-flex items-center gap-1 text-[#234333] hover:bg-[#234333]/10 text-[11px] font-semibold px-2 py-1 rounded-sm">
                      <Pencil size={11} /> Edit
                    </button>
                    <button type="button" onClick={() => setConfirmDelete(p)} data-testid={`adm-hero-slide-delete-${p.id}`} className="inline-flex items-center gap-1 text-red-700 hover:bg-red-50 text-[11px] font-semibold px-2 py-1 rounded-sm">
                      <Trash2 size={11} /> Delete
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#fbf8f1] max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{form.id ? "Edit Hero Slide" : "Add Hero Slide"}</DialogTitle>
            <DialogDescription>Upload a <span className="font-mono font-bold text-[#0B1F4D]">1200 × 1500 px</span> image (portrait, 4:5 ratio, under 300 KB). Other 4:5 sizes are fine — non-4:5 images will be cropped to fit.</DialogDescription>
          </DialogHeader>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Image (1200 × 1500 px · 300 KB max) *" full>
              <FileUploader kind="trust_hero_slide" label="hero image" url={form.image_url} onChange={({ url, public_id }) => { set("image_url", url || ""); set("image_public_id", public_id || ""); }} testid="adm-hero-slide-image" />
            </Field>
            <Field label="Title (optional)">
              <input data-testid="adm-hero-slide-input-title" type="text" maxLength={160} className={inputCls} value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Convocation 2024" />
            </Field>
            <Field label="Status">
              <select data-testid="adm-hero-slide-input-status" className={inputCls} value={form.status} onChange={(e) => set("status", e.target.value)}>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </Field>
            <Field label="Display Order">
              <input data-testid="adm-hero-slide-input-order" type="number" className={inputCls} value={form.order} onChange={(e) => set("order", e.target.value)} />
            </Field>
            <Field label="Caption (optional)" full>
              <input data-testid="adm-hero-slide-input-caption" type="text" maxLength={280} className={inputCls} value={form.caption} onChange={(e) => set("caption", e.target.value)} placeholder="Short overlay caption" />
            </Field>
          </div>
          <DialogFooter>
            <button type="button" onClick={() => setOpen(false)} className="text-sm px-4 py-2.5 rounded-sm border border-[#e5e0d8] text-[#6b655b] hover:bg-[#f5f1e8]">Cancel</button>
            <button type="button" onClick={save} disabled={saving} data-testid="adm-hero-slide-save-btn" className="text-sm bg-[#234333] hover:bg-[#1a3326] text-white px-4 py-2.5 rounded-sm font-semibold disabled:opacity-60">
              {saving ? "Saving…" : (form.id ? "Update" : "Add Slide")}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent className="bg-[#fbf8f1]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl">Delete Hero Slide?</AlertDialogTitle>
            <AlertDialogDescription>This will permanently remove this slide from the home page.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="adm-hero-slide-confirm-delete" className="bg-red-700 hover:bg-red-800 text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
